/**
 * #692 -- types and presentation rules for the POSP ledger statement
 * (`GET /commissions/ledger/statement/`), shared by the web pages and the Mini App.
 * Paid lines are grey in light AND dark mode; Dr/Cr and the Cr/Dr balance come from the server.
 */
export type LineStatus = 'due' | 'part_paid' | 'paid' | 'reversed';

export interface StatementRow {
  id: string;
  date: string;
  entry_type: string;
  entry_label: string;
  description: string;
  note: string;
  memo: boolean;
  policy_id: string | null;
  policy_number: string | null;
  customer: string | null;
  insurer: string | null;
  premium: string | null;
  debit: string | null;
  credit: string | null;
  running_balance: string;
  running_text: string;
  payout_reference: string | null;
  bank_confirmed: boolean;
  status: LineStatus | null;
  status_label: string | null;
  paid: string | null;
  remaining: string | null;
  reserved: string | null;
  paid_ref: string | null;
  paid_date: string | null;
  reversed: boolean;
}

export interface BalanceLine {
  label: string;
  date: string;
  amount: string;
  text: string;
}

export interface PospStatement {
  posp: { id: string; code: string; name: string };
  financial_year: string;
  financial_years: string[];
  date_from: string;
  date_to: string;
  balance_bf: BalanceLine;
  balance_cf: BalanceLine;
  totals: Record<'debit' | 'credit' | 'earned' | 'tds' | 'clawback' | 'payout', string>;
  due_total: string;
  trial_balance: { balanced: boolean; total_debit: string; total_credit: string };
  matches_ledger: boolean | null;
  year_close: { status: 'locked' | 'reopened'; closed_at: string | null; reopen_reason: string } | null;
  count: number;
  results: StatementRow[];
}

const GREY_ROW = 'bg-gray-50 text-gray-400 dark:bg-gray-800/40 dark:text-gray-500';
const NORMAL_ROW = 'text-gray-900 dark:text-gray-100';

/** Row classes: a Paid (or fully reversed) policy line is greyed, in both themes. */
export function rowClasses(row: Pick<StatementRow, 'status'>): string {
  return row.status === 'paid' || row.status === 'reversed' ? GREY_ROW : NORMAL_ROW;
}

export type BadgeVariant = 'success' | 'warning' | 'default' | 'error';

export function statusBadge(row: Pick<StatementRow, 'status' | 'status_label' | 'reversed'>): { label: string; variant: BadgeVariant } | null {
  if (!row.status || !row.status_label) return null;
  const variant: BadgeVariant = row.status === 'paid' ? 'default' : row.status === 'part_paid' ? 'warning' : row.status === 'reversed' ? 'error' : 'success';
  return { label: row.reversed && row.status === 'paid' ? `${row.status_label} - clawed back` : row.status_label, variant };
}

/** Second line under a policy: payout reference + date when paid, paid/remaining when part paid. */
export function statusDetail(row: StatementRow): string {
  if (row.status === 'paid' && row.paid_ref) return `${row.paid_ref}${row.paid_date ? ` · ${row.paid_date}` : ''}`;
  if (row.status === 'part_paid') return `Paid ${row.paid} · Remaining ${row.remaining}`;
  if (row.status === 'due' && row.reserved && Number(row.reserved) > 0) return `Reserved ${row.reserved} in a pending payout`;
  return '';
}

/** Current Indian financial year label for a date, e.g. 2026-10-29 -> "2026-27". */
export function currentFinancialYear(d: Date = new Date()): string {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(-2)}`;
}
